import {
  getPresignedUrl,
  uploadFileToS3,
  getDownloadUrl,
} from './receiptUploadServices'

type AttachmentType = 'image' | 'pdf'

const getAttachmentType = (mimeType: string): AttachmentType => {
  if (mimeType === 'application/pdf') return 'pdf'
  return 'image'
}

// Sanitize file names so S3 signed URLs don't break on special characters.
// e.g. "My Receipt (2).pdf" → "My_Receipt_2.pdf"
const sanitizeFileName = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.')
  const name = lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName
  const ext = lastDotIndex !== -1 ? fileName.slice(lastDotIndex) : ''

  const sanitized = name
    .replace(/[^a-zA-Z0-9._-]/g, '_') // Replace unsafe chars with underscores
    .replace(/_+/g, '_') // Collapse consecutive underscores
    .replace(/^_|_$/g, '') // Trim leading/trailing underscores

  return `${sanitized}${ext}`
}

interface UploadResult {
  status: 'success' | 'error'
  downloadUrl?: string
  error?: string
}

export const uploadReceiptToS3 = async (file: File): Promise<UploadResult> => {
  const attachmentType = getAttachmentType(file.type)
  const fileName = sanitizeFileName(file.name)

  // Get presigned URL
  const presignedResponse = await getPresignedUrl(fileName, attachmentType)
  if (!presignedResponse?.data?.presignedUrl) {
    return { status: 'error', error: 'No presigned URL returned' }
  }

  // Upload file to S3
  const uploadResponse = await uploadFileToS3(
    presignedResponse.data.presignedUrl,
    file
  )
  if (uploadResponse.status !== 200) {
    return { status: 'error', error: 'S3 upload failed' }
  }

  // Get download URL
  const downloadResponse = await getDownloadUrl(fileName, attachmentType)
  if (!downloadResponse?.data?.downloadUrl) {
    return { status: 'error', error: 'No download URL returned' }
  }

  return {
    status: 'success',
    downloadUrl: downloadResponse.data.downloadUrl,
  }
}
